import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const authApi = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Attach token to every request automatically
authApi.interceptors.request.use((config) => {
  const token = localStorage.getItem('xalent_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle 401 responses globally (auto-logout on expired token)
authApi.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response && error.response.status === 401) {
      localStorage.removeItem('xalent_token');
      localStorage.removeItem('xalent_user');
      window.location.reload();
    }
    return Promise.reject(error);
  }
);

export const authService = {
  // Login
  login: async (email, password) => {
    const response = await authApi.post('/auth/login', { email, password });
    const { token, user } = response.data;
    localStorage.setItem('xalent_token', token);
    localStorage.setItem('xalent_user', JSON.stringify(user));
    return { token, user };
  },

  // Logout
  logout: () => {
    localStorage.removeItem('xalent_token');
    localStorage.removeItem('xalent_user');
  },

  // Get current user from token
  getMe: async () => {
    const response = await authApi.get('/auth/me');
    return response.data;
  },

  // Change own password
  changePassword: async (currentPassword, newPassword) => {
    const response = await authApi.put('/auth/change-password', {
      current_password: currentPassword,
      new_password: newPassword,
    });
    return response.data;
  },

  // Check if user is logged in
  isLoggedIn: () => {
    return !!localStorage.getItem('xalent_token');
  },

  // Get stored user
  getStoredUser: () => {
    const user = localStorage.getItem('xalent_user');
    return user ? JSON.parse(user) : null;
  },

  // Get stored token
  getToken: () => {
    return localStorage.getItem('xalent_token');
  },

  // ---- Admin endpoints ----

  // List all users
  getUsers: async () => {
    const response = await authApi.get('/admin/users');
    return response.data;
  },

  // Create a user
  createUser: async (name, email, password, role = 'user', credits = 10) => {
    const response = await authApi.post('/admin/users', {
      name,
      email,
      password,
      role,
      credits,
    });
    return response.data;
  },

  // Get single user
  getUser: async (userId) => {
    const response = await authApi.get(`/admin/users/${userId}`);
    return response.data;
  },

  // Update user
  updateUser: async (userId, data) => {
    const response = await authApi.put(`/admin/users/${userId}`, data);
    return response.data;
  },

  // Delete user
  deleteUser: async (userId) => {
    const response = await authApi.delete(`/admin/users/${userId}`);
    return response.data;
  },

  // Get admin stats
  getStats: async () => {
    const response = await authApi.get('/admin/stats');
    return response.data;
  },

  // ---- Credit endpoints ----

  // Get my credit balance
  getMyCredits: async () => {
    const response = await authApi.get('/credits/me');
    return response.data;
  },

  // Request credits from admin
  requestCredits: async (amount, reason) => {
    const response = await authApi.post('/credits/request', { amount, reason });
    return response.data;
  },

  // Get my credit request history
  getMyRequests: async () => {
    const response = await authApi.get('/credits/my-requests');
    return response.data;
  },

  // Use one credit (for influencer video pre-deduction)
  useCredit: async () => {
    const response = await authApi.post('/credits/use');
    return response.data;
  },

  // ---- Admin credit endpoints ----

  // Get all credit requests (admin)
  getCreditRequests: async (status) => {
    const url = status ? `/admin/credit-requests?status=${status}` : '/admin/credit-requests';
    const response = await authApi.get(url);
    return response.data;
  },

  // Approve or deny a credit request (admin)
  resolveCreditRequest: async (requestId, action, adminNote) => {
    const response = await authApi.put(`/admin/credit-requests/${requestId}`, {
      action,
      admin_note: adminNote,
    });
    return response.data;
  },

  // Set user credits directly (admin)
  setUserCredits: async (userId, credits) => {
    const response = await authApi.put(`/admin/users/${userId}/credits`, { credits });
    return response.data;
  },
};

export default authApi;
